/**
 * Review moderation — the queue, and the three things that can happen to a review.
 *
 * **Redaction removes contact details and outcome claims. It never softens the complaint.** That
 * distinction is the whole ethic of this screen: a review saying a pandit arrived late stays,
 * exactly as written; a phone number and "he promised my son would clear his exam" do not — the
 * second because we may never publish an outcome claim, ours or a devotee's.
 *
 * **There is no client-side redaction helper.** This screen used to run a regex that stripped the
 * phrases it recognised and offered the result as a suggestion. That is worse than nothing: it
 * misses what it was not written for, and a moderator would publish text believing it had been
 * cleaned. The moderator types what should stand, the server stores the original for audit, and
 * the content lint refuses the result if a claim survives.
 *
 * **Publish, reject and remove are three separate acts.** Publishing moves a pandit's public
 * rating and can raise a quality flag; rejecting refuses something a person wrote about their own
 * puja; removing is a takedown of something already public. A single status dropdown would make
 * them look interchangeable.
 */
import { useState } from "react";
import { PanditReplies } from "./SupportDesk";
import {
  Button,
  Rating,
  StatusChip,
  type Tone,
} from "../components/primitives";
import { Input, Select } from "../components/forms";
import { DataTable, DetailPage } from "../components/data";
import { KV } from "../components/entities";
import { EmptyState, ErrorState, LoadingState } from "../components/feedback";
import {
  ConfirmAction,
  FilterBar,
  ScopeAction,
  Section,
  StatTile,
} from "../components/kit";
import { useConsole } from "../app/console";
import { errorMessage } from "../lib/api/client";
import {
  useCreateDimension,
  useModerateReview,
  useProfileDivergence,
  usePublishReview,
  useQualityFlagQueue,
  useRatingDimensions,
  useReviewQueue,
  useReviewSummary,
  useUpdateDimension,
  type AdminReviewRow,
} from "../lib/api/hooks/reviews";
import { flagRows } from "./qualityFlags";
import { formatDateTime } from "../lib/datetime";

const PAGE_SIZE = 50;

/** The server's set (`ModerationReason`), not a friendlier one — ops read these in the logs. */
export const MODERATION_REASONS = [
  "abusive_language",
  "pii_disclosure",
  "off_platform_contact",
  "spam_or_fake",
  "unrelated_content",
  "legal_takedown",
] as const;

const QUEUES = [
  { key: "pending_moderation", label: "Waiting" },
  { key: "published", label: "Published" },
  { key: "rejected", label: "Rejected" },
  { key: "removed", label: "Removed" },
];

export function statusTone(status: string): Tone {
  if (status === "published") return "success";
  if (status === "rejected" || status === "removed") return "error";
  return "warn";
}

/** A review that has been edited was approved once for words that are no longer there. */
export function editedNote(row: AdminReviewRow): string | null {
  return row.is_edited ? "edited since it was written — read it again" : null;
}

/** "Reads like review #12, #15" — the reviews the server found this one resembles (doc 33 §5
 * rule 3). Advisory: it says why a person is reading it, not what they should decide. */
export function readsLike(ids: readonly number[] = []): string | null {
  return ids.length === 0
    ? null
    : `Reads like review ${ids.map((id) => `#${id}`).join(", ")}`;
}

/** The design's "Why it is here": the hold the server recorded, and any resemblance it found. */
export function whyHere(row: AdminReviewRow): string {
  const like = readsLike(row.similar_review_ids);
  const parts = [row.moderation_reason?.replace(/_/g, " "), like].filter(
    Boolean,
  );
  return parts.length > 0 ? parts.join(" · ") : "—";
}

export function Reviews() {
  const { scope, toast } = useConsole();
  const [queue, setQueue] = useState("pending_moderation");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState("Moderate");
  const [redaction, setRedaction] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [moderating, setModerating] = useState<"reject" | "remove" | null>(
    null,
  );
  const [reason, setReason] = useState<string>(MODERATION_REASONS[0]);
  const [newCode, setNewCode] = useState("");

  const list = useReviewQueue({
    review_status: queue,
    page,
    page_size: PAGE_SIZE,
  });
  const dimensions = useRatingDimensions();
  const publish = usePublishReview();
  const moderate = useModerateReview();
  const createDimension = useCreateDimension();
  const updateDimension = useUpdateDimension();

  const fail = (error: unknown) =>
    toast({ tone: "error", message: errorMessage(error) });

  const open =
    openId === null
      ? null
      : (list.data?.items.find((r) => r.id === openId) ?? null);

  if (openId !== null) {
    const note = open === null ? null : editedNote(open);
    return (
      <DetailPage
        title={`Review #${openId}`}
        onClose={() => {
          setOpenId(null);
          setRedaction("");
        }}
        actions={
          open === null ? null : (
            <>
              <ScopeAction
                scope={scope}
                need="support"
                size="sm"
                variant="secondary"
                disabled={open.status === "published"}
                onClick={() => setPublishing(true)}
              >
                Publish
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="support"
                size="sm"
                variant="ghost"
                onClick={() =>
                  setModerating(
                    open.status === "published" ? "remove" : "reject",
                  )
                }
              >
                {open.status === "published" ? "Take down" : "Reject"}
              </ScopeAction>
            </>
          )
        }
      >
        {open === null && (
          <Section
            title="This review has moved"
            note="It is no longer in the queue you were in"
          >
            <p style={{ margin: 0, fontSize: "var(--fs-admin-ui)" }}>
              Your decision went through and the review left this list. Close to
              go back.
            </p>
          </Section>
        )}

        {open !== null && (
          <>
            <Section title="What was written" note={note ?? undefined}>
              <KV
                rows={[
                  ["Rating", <Rating key="r" value={open.overall_rating} />],
                  ["Title", open.title ?? "—"],
                  /* Live 2026-10-30 a moderator saw the words and nothing of whom they were about. */
                  ["Pandit", open.pandit_display_name ?? `pandit #${open.pandit_profile_id}`],
                  ["Booking", open.booking_public_code ?? `booking #${open.booking_id}`],
                  ...(open.puja_name ? [["Puja", open.puja_name] as const] : []),
                  ["Written", formatDateTime(open.created_at)],
                  ["State", open.status.replace(/_/g, " ")],
                  ...(open.moderation_reason
                    ? [
                        [
                          "Refused because",
                          open.moderation_reason.replace(/_/g, " "),
                        ] as const,
                      ]
                    : []),
                  ...(open.similar_review_ids?.length
                    ? [
                        [
                          "Possibly copied",
                          readsLike(open.similar_review_ids),
                        ] as const,
                      ]
                    : []),
                ]}
              />
              <p
                style={{
                  marginTop: "var(--sp-md)",
                  fontSize: "var(--fs-admin-ui)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {open.body ?? "No text — a rating only."}
              </p>
            </Section>

            <Section
              title="Redact before publishing"
              note="Take out contact details and outcome claims. Never the complaint."
            >
              {/* No suggestion button. A regex that strips what it recognises misses what it does
              not, and the moderator would publish believing the text had been cleaned. */}
              <Input
                label="Replacement text — leave empty to publish as written"
                value={redaction}
                onChange={setRedaction}
                style={{ width: "100%" }}
              />
              <p
                style={{
                  fontSize: "var(--fs-admin-colhead)",
                  color: "var(--c-ink2)",
                }}
              >
                The original is kept for audit and never shown to anybody. If a
                prohibited claim survives your edit the server refuses the
                publish and says which phrase.
              </p>
            </Section>
          </>
        )}

        {publishing && open !== null && (
          <ConfirmAction
            open
            title="Publish this review?"
            confirmLabel="Publish"
            phrase="PUBLISH"
            consequences={[
              "It becomes public on the pandit’s page and moves their rating.",
              "A low rating also raises a quality flag for supply to look at.",
              redaction
                ? "Your replacement text is what the public sees; the original is kept for audit."
                : "It publishes exactly as written.",
            ]}
            onCancel={() => setPublishing(false)}
            onConfirm={() =>
              publish.mutate(
                { reviewId: openId, redactedBody: redaction || undefined },
                {
                  onSuccess: () => {
                    setPublishing(false);
                    setRedaction("");
                    toast({ tone: "success", message: "Published" });
                  },
                  onError: (error) => {
                    setPublishing(false);
                    fail(error);
                  },
                },
              )
            }
          />
        )}

        {moderating !== null && open !== null && (
          <ConfirmAction
            open
            title={
              moderating === "remove"
                ? "Take this review down?"
                : "Reject this review?"
            }
            confirmLabel={moderating === "remove" ? "Take down" : "Reject"}
            phrase={moderating === "remove" ? "TAKE DOWN" : "REJECT"}
            consequences={
              moderating === "remove"
                ? [
                    "It stops being public and the pandit’s rating is recomputed without it.",
                    "The author is told, with the reason you choose.",
                  ]
                : [
                    "It never becomes public. The author is told, with the reason you choose.",
                    "Rejecting is not the answer to an outcome claim — redact and publish instead.",
                  ]
            }
            reason="Anything the author should know"
            onCancel={() => setModerating(null)}
            onConfirm={(why) =>
              moderate.mutate(
                { reviewId: openId, decision: moderating, reason, note: why },
                {
                  onSuccess: () => {
                    setModerating(null);
                    toast({ tone: "success", message: "Recorded" });
                  },
                  onError: (error) => {
                    setModerating(null);
                    fail(error);
                  },
                },
              )
            }
          >
            <Select
              label="Reason — the author is told this"
              value={reason}
              options={[...MODERATION_REASONS]}
              onChange={setReason}
            />
          </ConfirmAction>
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <ReviewTiles />
      <FilterBar
        tabs={QUEUES}
        tab={queue}
        onTab={(key) => {
          setQueue(key);
          setPage(1);
        }}
        extra={
          <>
            <ScopeAction
              scope={scope}
              need="support"
              size="sm"
              variant="ghost"
              onClick={() =>
                setDetailTab(
                  detailTab === "Moderate" ? "Dimensions" : "Moderate",
                )
              }
            >
              {detailTab === "Moderate" ? "Dimensions" : "Queue"}
            </ScopeAction>
            {/* Its own view, not a section under the queue: replies are a second queue, read on purpose. */}
            <ScopeAction
              scope={scope}
              need="support"
              size="sm"
              variant="ghost"
              onClick={() =>
                setDetailTab(detailTab === "Replies" ? "Moderate" : "Replies")
              }
            >
              {detailTab === "Replies" ? "Reviews" : "Pandit replies"}
            </ScopeAction>
          </>
        }
      />

      {detailTab === "Replies" ? (
        <PanditReplies />
      ) : detailTab === "Dimensions" ? (
        <Section
          title="What a review can score"
          note="Data, not a migration. Labels are set in enum_label, localised."
        >
          {dimensions.isPending && (
            <LoadingState message="Reading the dimensions…" />
          )}
          {dimensions.isError && (
            <ErrorState
              title="Could not read the dimensions"
              cause={errorMessage(dimensions.error)}
            />
          )}
          {dimensions.isSuccess && (
            <>
              <DataTable
                density="compact"
                columns={[
                  { key: "code", label: "Code", mono: true },
                  { key: "order", label: "Order", align: "right" },
                  { key: "state", label: "State" },
                  { key: "act", label: "" },
                ]}
                rows={dimensions.data.items.map((d) => ({
                  id: d.id,
                  code: d.code,
                  order: d.sort_order,
                  state: d.is_active ? "in use" : "hidden from new reviews",
                  act: (
                    <ScopeAction
                      scope={scope}
                      need="support"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        updateDimension.mutate(
                          { id: d.id, isActive: !d.is_active },
                          {
                            onSuccess: () =>
                              toast({ tone: "success", message: "Updated" }),
                            onError: fail,
                          },
                        )
                      }
                    >
                      {d.is_active ? "Hide" : "Show"}
                    </ScopeAction>
                  ),
                }))}
              />
              <div
                style={{
                  display: "flex",
                  gap: "var(--sp-md)",
                  alignItems: "end",
                  marginTop: "var(--sp-md)",
                }}
              >
                <Input
                  label="New dimension code"
                  value={newCode}
                  onChange={setNewCode}
                />
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  disabled={newCode.trim() === ""}
                  onClick={() =>
                    createDimension.mutate(
                      { code: newCode.trim(), sortOrder: 50 },
                      {
                        onSuccess: () => {
                          setNewCode("");
                          toast({ tone: "success", message: "Added" });
                        },
                        onError: fail,
                      },
                    )
                  }
                >
                  Add
                </ScopeAction>
              </div>
              {/* Deactivation, never deletion — a scored dimension is part of what those
                  reviews said, and the foreign key refuses to let it go. */}
              <p
                style={{
                  fontSize: "var(--fs-admin-colhead)",
                  color: "var(--c-ink2)",
                }}
              >
                A dimension that has been scored is hidden, never deleted —
                removing it would take part of what those reviews said with it.
              </p>
            </>
          )}
        </Section>
      ) : (
        <>
          {list.isPending && <LoadingState message="Reading the queue…" />}
          {list.isError && (
            <ErrorState
              title="Could not read the queue"
              cause={errorMessage(list.error)}
            />
          )}
          {list.isSuccess && list.data.items.length === 0 && (
            <EmptyState
              title="Nothing waiting"
              body="Reviews arrive here as devotees write them. Nothing publishes itself unless auto-publish is switched on, and a low rating is always read by a person."
            />
          )}
          {list.isSuccess && list.data.items.length > 0 && (
            <DataTable
              density="compact"
              onRowClick={(row) => setOpenId(Number(row.id))}
              columns={[
                { key: "rating", label: "Rating" },
                { key: "title", label: "What it says" },
                { key: "why", label: "Why it is here" },
                { key: "state", label: "State" },
              ]}
              rows={list.data.items.map((r) => ({
                id: r.id,
                rating: <Rating value={r.overall_rating} />,
                title: r.title ?? r.body?.slice(0, 60) ?? "A rating only",
                why: whyHere(r),
                state: (
                  <StatusChip
                    tone={statusTone(r.status)}
                    label={r.status.replace(/_/g, " ")}
                  />
                ),
              }))}
              footer={`${list.data.total} review${list.data.total === 1 ? "" : "s"}`}
            />
          )}
          <QualityFlags />
          <CircumventionWatch />
        </>
      )}
    </>
  );
}

const SUSPENDED = new Set(["temporary_suspension", "delisting"]);

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** Admin doc 20 §4 item 2: pandit ratings are rebuilt nightly from the review rows, so the
 * honest integrity line is when that last happened — never a "verified" tick. */
const recomputed = (at: string | null | undefined) =>
  at
    ? `pandit scores rebuilt ${new Date(at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} IST`
    : "pandit scores not built yet";

/** Admin doc 20 §4 item 2, the check itself: pandits whose cached rating count disagrees with
 * their published reviews, counted by the server when the summary is read (alert row 53's query). */
export const integrity = (drifted: number | undefined) =>
  drifted
    ? `${plural(drifted, "pandit's rating count disagrees", "pandits' rating counts disagree")} with their reviews`
    : "rating counts match the reviews";

/** The design's KPI row. Counted on the server over the whole tables (the queue below holds one
 * page); the flag tile reads the flag queue the section under the table already fetched. */
function ReviewTiles() {
  const summary = useReviewSummary();
  const flags = useQualityFlagQueue();
  const s = summary.data;
  const suspended = new Set(
    (flags.data?.items ?? [])
      .filter(
        (g) => g.standing_action !== null && SUSPENDED.has(g.standing_action),
      )
      .map((g) => g.pandit_id),
  ).size;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
        gap: "var(--sp-md)",
      }}
    >
      <StatTile
        label="Awaiting moderation"
        value={s?.awaiting ?? "—"}
        note={
          s ? `${s.awaiting_contact_hold} held for contact details` : undefined
        }
        tone={s && s.awaiting > 0 ? "error" : "default"}
      />
      <StatTile
        label="Contested by pandits"
        value={s?.contested ?? "—"}
        note="pandit replies awaiting a moderator"
      />
      <StatTile
        label="Average rating"
        value={s?.average_rating == null ? "—" : s.average_rating.toFixed(1)}
        note={
          s
            ? `${plural(s.published, "published review", "published reviews")} · ${recomputed(s.scores_computed_at)} · ${integrity(s.rating_counts_drifted)}`
            : undefined
        }
        tone={s && (s.rating_counts_drifted ?? 0) > 0 ? "error" : "default"}
      />
      <StatTile
        label="Open quality flags"
        value={flags.data?.open_total ?? "—"}
        note={
          flags.data
            ? `${plural(suspended, "pandit", "pandits")} suspended or delisted`
            : undefined
        }
        tone={(flags.data?.open_total ?? 0) > 0 ? "error" : "default"}
      />
    </div>
  );
}

/** Support raises them, supply consumes them in the directory — this is the hand-off. */
function QualityFlags() {
  const { go } = useConsole();
  const flags = useQualityFlagQueue();
  return (
    <Section
      title="Quality flags"
      note="Support raises them, supply consumes them in the pandit directory"
      pad={false}
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon="users"
          onClick={() => go("pandits")}
        >
          Open directory
        </Button>
      }
    >
      {flags.isPending && <LoadingState message="Reading the flags…" />}
      {flags.isError && (
        <ErrorState
          title="Could not read the flags"
          cause={errorMessage(flags.error)}
        />
      )}
      {flags.isSuccess && flags.data.items.length === 0 && (
        <EmptyState
          title="No open flags"
          body="A no-show, a late cancellation or a poor published review raises one here."
        />
      )}
      {flags.isSuccess && flags.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: "pandit", label: "Pandit" },
            { key: "kind", label: "Flag" },
            { key: "src", label: "Source" },
            { key: "countCell", label: "In 90 days", align: "right" },
            { key: "status", label: "Standing action" },
          ]}
          rows={flagRows(flags.data.items).map((f) => ({
            ...f,
            countCell: (
              <span
                className="tabular"
                style={{ display: "block", textAlign: "right" }}
              >
                {f.count}
              </span>
            ),
            status: (
              <span style={{ display: "inline-flex", gap: "var(--sp-xs)", flexWrap: "wrap" }}>
                <StatusChip tone={f.tone} label={f.action} />
                {/* A suggestion to the reader, never an action taken (backend 33). */}
                {f.escalate && (
                  <span title="Open flags have reached this flag type's escalation threshold">
                    <StatusChip tone="warn" label={`Consider escalating · ${f.escalate}`} />
                  </span>
                )}
              </span>
            ),
          }))}
          footer={`${flags.data.open_total} open flag${flags.data.open_total === 1 ? "" : "s"}`}
        />
      )}
    </Section>
  );
}

/** The windows the server allows (`days` 7–90); 30 is its default. */
export const DIVERGENCE_WINDOWS = ["7 days", "30 days", "90 days"] as const;

/** Views against bookings per pandit (12-content/15 §7.5, CSR-143 ask 6). A mark is a reason to
 * look, never an action: nothing here suspends, warns or tells the pandit, and the server's own
 * basis line is printed under the table so nobody reads it as more. */
function CircumventionWatch() {
  const [days, setDays] = useState<string>("30 days");
  const report = useProfileDivergence(parseInt(days, 10));
  return (
    <Section
      title="Circumvention watch"
      note="Profile views against bookings — a signal to investigate, never a penalty"
      pad={false}
      actions={
        <Select
          label="Window"
          value={days}
          onChange={setDays}
          options={DIVERGENCE_WINDOWS}
        />
      }
    >
      {report.isPending && <LoadingState message="Counting views…" />}
      {report.isError && (
        <ErrorState
          title="Could not read the watch"
          cause={errorMessage(report.error)}
        />
      )}
      {report.isSuccess && report.data.items.length === 0 && (
        <EmptyState
          title="Nobody over the floor"
          body={`No pandit had ${report.data.min_views} or more profile views in ${report.data.days} days.`}
        />
      )}
      {report.isSuccess && report.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: "pandit", label: "Pandit" },
            { key: "views", label: "Views", align: "right" },
            { key: "bookings", label: "Bookings", align: "right" },
            { key: "rateCell", label: "Booked per view", align: "right" },
            { key: "mark", label: "Look at" },
          ]}
          rows={report.data.items.map((r) => ({
            id: r.pandit_profile_id,
            pandit: `${r.display_name} · ${r.public_code}`,
            views: r.views,
            bookings: r.bookings,
            rateCell: (
              <span className="tabular">{(r.rate * 100).toFixed(1)}%</span>
            ),
            mark: r.marked ? (
              <StatusChip
                tone="warn"
                label={`under ${(report.data.flag_rate * 100).toFixed(1)}%`}
              />
            ) : (
              "—"
            ),
          }))}
          footer={report.data.basis}
        />
      )}
    </Section>
  );
}
